import { Injectable, signal, effect, computed } from '@angular/core';

/** Visual style — controls fonts, primary/accent colors, grain */
export interface CmtStyle {
  id: string;
  label: string;
  fontSans: string;
  fontDisplay: string;
  fontMono: string;
  fontImportUrl: string | null;
  grain: boolean;
  /** Border-radius scale (`--radius`) — sets the overall shape language */
  radius: string;
  /** Optional per-style surface tint (hue shift on background/muted/border) */
  lightSurfaces?: Partial<Record<string, string>>;
  /** Style-specific color overrides (primary, accent, destructive) */
  colors: {
    primary: string;
    primaryForeground: string;
    accent: string;
    accentForeground: string;
    ring: string;
    destructive: string;
  };
}

/** Color mode — controls surface/text/border colors */
export type ColorMode = 'light' | 'dark';

const LIGHT_SURFACES = {
  '--background': 'oklch(0.985 0.005 85)',
  '--foreground': 'oklch(0.20 0.02 270)',
  '--card': 'oklch(1 0 0)',
  '--card-foreground': 'oklch(0.20 0.02 270)',
  '--popover': 'oklch(1 0 0)',
  '--popover-foreground': 'oklch(0.20 0.02 270)',
  '--secondary': 'oklch(0.96 0.008 85)',
  '--secondary-foreground': 'oklch(0.20 0.02 270)',
  '--muted': 'oklch(0.95 0.008 85)',
  '--muted-foreground': 'oklch(0.50 0.02 270)',
  '--border': 'oklch(0.88 0.005 85)',
  '--input': 'oklch(0.84 0.008 85)',
  '--success': 'oklch(0.45 0.14 155)',
  '--warning': 'oklch(0.65 0.15 75)',
};

const DARK_SURFACES = {
  '--background': 'oklch(0.16 0.014 270)',
  '--foreground': 'oklch(0.93 0.005 250)',
  '--card': 'oklch(0.20 0.012 270)',
  '--card-foreground': 'oklch(0.93 0.005 250)',
  '--popover': 'oklch(0.22 0.012 270)',
  '--popover-foreground': 'oklch(0.93 0.005 250)',
  '--secondary': 'oklch(0.24 0.01 270)',
  '--secondary-foreground': 'oklch(0.88 0.005 250)',
  '--muted': 'oklch(0.25 0.01 270)',
  '--muted-foreground': 'oklch(0.62 0.01 250)',
  '--border': 'oklch(0.30 0.01 270)',
  '--input': 'oklch(0.28 0.01 270)',
  '--success': 'oklch(0.72 0.14 155)',
  '--warning': 'oklch(0.78 0.12 75)',
};

// AMIE brand palette — derived from the logo
//   navy  = oklch(0.30 0.18 265)  (deep dark-blue "AMIE" wordmark)
//   cyan  = oklch(0.68 0.17 245)  (bright electric-blue glow)
// Clean style — AMIE brand palette (anchored on the logo, #0a215e navy + cyan glow)
const AMIE_LIGHT_COLORS = {
  primary: 'oklch(0.24 0.13 268)',     // ≈ #0a215e
  primaryForeground: 'oklch(0.99 0.005 250)',
  accent: 'oklch(0.55 0.13 252)',
  accentForeground: 'oklch(0.99 0.005 250)',
  ring: 'oklch(0.24 0.13 268)',
  destructive: 'oklch(0.56 0.20 27)',
};

const AMIE_DARK_COLORS = {
  primary: 'oklch(0.62 0.13 250)',
  primaryForeground: 'oklch(0.14 0.02 265)',
  accent: 'oklch(0.45 0.12 255)',
  accentForeground: 'oklch(0.97 0.005 250)',
  ring: 'oklch(0.62 0.13 250)',
  destructive: 'oklch(0.62 0.20 27)',
};

// Editorial style — forest green + amber, classic print pairing,
// hue 155 keeps a wide signal-distance to the destructive red (hue 27).
const EDITORIAL_LIGHT_COLORS = {
  primary: 'oklch(0.40 0.10 155)',     // forest green
  primaryForeground: 'oklch(0.99 0.005 155)',
  accent: 'oklch(0.78 0.12 75)',       // warm amber
  accentForeground: 'oklch(0.22 0.04 70)',
  ring: 'oklch(0.40 0.10 155)',
  destructive: 'oklch(0.56 0.20 27)',
};

const EDITORIAL_DARK_COLORS = {
  primary: 'oklch(0.66 0.13 155)',     // moss/sage for dark surfaces
  primaryForeground: 'oklch(0.14 0.02 155)',
  accent: 'oklch(0.72 0.14 75)',
  accentForeground: 'oklch(0.16 0.02 75)',
  ring: 'oklch(0.66 0.13 155)',
  destructive: 'oklch(0.60 0.20 27)',
};

// Cool neutral surfaces for the Clean style (hue 250 instead of warm 85)
const CLEAN_LIGHT_SURFACES = {
  '--background': 'oklch(0.985 0.006 250)',
  '--secondary': 'oklch(0.96 0.010 250)',
  '--muted': 'oklch(0.95 0.010 250)',
  '--border': 'oklch(0.88 0.008 250)',
  '--input': 'oklch(0.84 0.012 250)',
};

const EDITORIAL: CmtStyle = {
  id: 'editorial',
  label: 'Editorial',
  fontSans: "'Geist', 'Segoe UI', system-ui, sans-serif",
  fontDisplay: "'Fraunces', Georgia, serif",
  fontMono: "'Geist Mono', 'JetBrains Mono', monospace",
  fontImportUrl: null,
  grain: true,  // only in light mode
  radius: '0.4rem',
  colors: EDITORIAL_LIGHT_COLORS,
};

const CLEAN_TEAL: CmtStyle = {
  id: 'clean-teal',
  label: 'Clean',
  fontSans: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
  fontDisplay: "'DM Sans', 'Segoe UI', system-ui, sans-serif",
  fontMono: "'JetBrains Mono', 'Fira Code', monospace",
  fontImportUrl: 'https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap',
  grain: false,
  radius: '0.75rem',
  lightSurfaces: CLEAN_LIGHT_SURFACES,
  colors: AMIE_LIGHT_COLORS,
};

const CLEAN_TEAL_DARK_COLORS = AMIE_DARK_COLORS;

export const STYLES: CmtStyle[] = [EDITORIAL, CLEAN_TEAL];

const STYLE_KEY = 'cmt-style';
const MODE_KEY = 'cmt-color-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly styles = STYLES;

  readonly style = signal<CmtStyle>(this.loadStyle());
  readonly mode = signal<ColorMode>(this.loadMode());

  /** Computed: is dark mode active */
  readonly isDark = computed(() => this.mode() === 'dark');

  private fontLinkEl: HTMLLinkElement | null = null;

  constructor() {
    effect(() => this.applyTheme(this.style(), this.mode()));
  }

  /** Cycle through styles (Editorial → Clean Teal → …) */
  toggleStyle(): void {
    const idx = STYLES.findIndex(s => s.id === this.style().id);
    this.style.set(STYLES[(idx + 1) % STYLES.length]);
  }

  /** Toggle light ↔ dark */
  toggleMode(): void {
    this.mode.set(this.mode() === 'light' ? 'dark' : 'light');
  }

  selectStyle(id: string): void {
    const s = STYLES.find(s => s.id === id);
    if (s) this.style.set(s);
  }

  private applyTheme(style: CmtStyle, mode: ColorMode): void {
    const root = document.documentElement;
    const isDark = mode === 'dark';

    // 1. Surface colors (from mode + optional per-style light-surface tint)
    const baseSurfaces = isDark ? DARK_SURFACES : LIGHT_SURFACES;
    const surfaces = !isDark && style.lightSurfaces
      ? { ...baseSurfaces, ...style.lightSurfaces }
      : baseSurfaces;
    for (const [key, value] of Object.entries(surfaces)) {
      root.style.setProperty(key, value);
    }

    // 2. Style colors (primary, accent — adjusted for dark)
    const colors = isDark ? this.getDarkColors(style) : style.colors;
    root.style.setProperty('--primary', colors.primary);
    root.style.setProperty('--primary-foreground', colors.primaryForeground);
    root.style.setProperty('--accent', colors.accent);
    root.style.setProperty('--accent-foreground', colors.accentForeground);
    root.style.setProperty('--ring', colors.ring);
    root.style.setProperty('--destructive', colors.destructive);

    // 3. Fonts
    root.style.setProperty('--font-sans', style.fontSans);
    root.style.setProperty('--font-display', style.fontDisplay);
    root.style.setProperty('--font-mono', style.fontMono);
    document.body.style.fontFamily = style.fontSans;

    // 4. Load extra font if needed
    if (style.fontImportUrl) {
      if (!this.fontLinkEl) {
        this.fontLinkEl = document.createElement('link');
        this.fontLinkEl.rel = 'stylesheet';
        document.head.appendChild(this.fontLinkEl);
      }
      this.fontLinkEl.href = style.fontImportUrl;
    }

    // 5. Border-radius scale (shape language)
    root.style.setProperty('--radius', style.radius);

    // 6. Grain overlay — only in light mode + editorial
    document.body.classList.toggle('no-grain', isDark || !style.grain);

    // 7. Persist
    localStorage.setItem(STYLE_KEY, style.id);
    localStorage.setItem(MODE_KEY, mode);
  }

  private getDarkColors(style: CmtStyle): typeof style.colors {
    if (style.id === 'editorial') return EDITORIAL_DARK_COLORS;
    if (style.id === 'clean-teal') return CLEAN_TEAL_DARK_COLORS;
    return style.colors; // fallback
  }

  private loadStyle(): CmtStyle {
    try {
      const id = localStorage.getItem(STYLE_KEY);
      if (id) return STYLES.find(s => s.id === id) ?? EDITORIAL;
    } catch {
      // localStorage unavailable (e.g. private mode) — fall through to default
    }
    return EDITORIAL;
  }

  private loadMode(): ColorMode {
    try {
      const m = localStorage.getItem(MODE_KEY);
      if (m === 'dark') return 'dark';
    } catch {
      // localStorage unavailable (e.g. private mode) — fall through to default
    }
    return 'light';
  }
}
